import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { UserManagementActivityPoint } from 'api/userCredentials';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  ChartOptions,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { map } from 'es-toolkit/compat';
import React, { useMemo } from 'react';
import { Chart as ReactChart } from 'react-chartjs-2';
import { formatDateForUser } from 'utils/dateTime';

Chart.register(
  CategoryScale,
  LinearScale,
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

interface UserActivityTimelineChartProps {
  points: UserManagementActivityPoint[];
  canViewInternalMetrics: boolean;
  title: string;
  height?: number;
}

const UserActivityTimelineChart: React.FC<UserActivityTimelineChartProps> = ({
  points,
  canViewInternalMetrics,
  title,
  height = 360,
}) => {
  const theme = useTheme();
  const textColor = theme.palette.text.secondary;
  const gridColor = theme.palette.divider;

  const chartData = useMemo(
    () => ({
      labels: map(points, (point) =>
        formatDateForUser(point.date, {
          month: 'short',
          day: 'numeric',
        })
      ),
      datasets: [
        {
          type: 'line' as const,
          label: 'Active Users',
          data: map(points, 'activeUsers'),
          borderColor: theme.palette.primary.main,
          backgroundColor: theme.palette.primary.main,
          pointRadius: 2,
          tension: 0.3,
          yAxisID: 'y',
          order: 1,
        },
        {
          type: 'line' as const,
          label: 'External Users',
          data: map(points, 'activeExternalUsers'),
          borderColor: theme.palette.info.main,
          backgroundColor: theme.palette.info.main,
          pointRadius: 2,
          tension: 0.3,
          yAxisID: 'y',
          order: 2,
        },
        ...(canViewInternalMetrics
          ? [
              {
                type: 'line' as const,
                label: 'Internal Users',
                data: map(points, 'activeInternalUsers'),
                borderColor: theme.palette.secondary.main,
                backgroundColor: theme.palette.secondary.main,
                pointRadius: 2,
                tension: 0.3,
                yAxisID: 'y',
                order: 3,
              },
            ]
          : []),
        {
          type: 'bar' as const,
          label: 'New Studies',
          data: map(points, 'newStudies'),
          backgroundColor: `${theme.palette.warning.main}66`,
          borderRadius: 3,
          yAxisID: 'y1',
          order: 4,
        },
      ],
    }),
    [points, canViewInternalMetrics, theme]
  );

  const options: ChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index' as const, intersect: false },
      plugins: {
        title: { display: true, text: title, color: textColor, font: { size: 14 } },
        legend: { position: 'bottom' as const, labels: { color: textColor } },
      },
      scales: {
        x: { ticks: { color: textColor }, grid: { color: gridColor } },
        y: {
          type: 'linear' as const,
          position: 'left' as const,
          title: { display: true, text: 'Users', color: textColor },
          ticks: { color: textColor },
          grid: { color: gridColor },
        },
        y1: {
          type: 'linear' as const,
          position: 'right' as const,
          title: { display: true, text: 'New Studies', color: textColor },
          ticks: { color: textColor },
          grid: { drawOnChartArea: false },
        },
      },
    }),
    [gridColor, textColor, title]
  );

  if (points.length === 0) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">No trend data</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height, position: 'relative' }}>
      <ReactChart type="bar" data={chartData} options={options} />
    </Box>
  );
};

export default UserActivityTimelineChart;
